import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiHome, FiBook, FiFileText, FiEdit3, FiFeather, FiBookOpen, FiMenu, FiX } = FiIcons;

const Navigation = ({ activeTab, setActiveTab }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const mainTabs = [
    { id: 'Dashboard', label: 'Dashboard', icon: FiHome, path: '/' },
    { id: 'Summary', label: 'My Journey', icon: FiFileText, path: '/summary' }
  ];

  const lessonTabs = [
    { id: 'Lesson 1', label: 'Readiness Audit', icon: FiEdit3, path: '/lesson/1' },
    { id: 'Lesson 2', label: 'Reader Experience', icon: FiBookOpen, path: '/lesson/2' },
    { id: 'Lesson 3', label: 'Self-Editing', icon: FiFeather, path: '/lesson/3' },
    { id: 'Lesson 4', label: 'Voice & Craft', icon: FiEdit3, path: '/lesson/4' },
    { id: 'Lesson 5', label: 'Publishing Paths', icon: FiBook, path: '/lesson/5' },
    { id: 'Lesson 6', label: 'Book Setup', icon: FiBook, path: '/lesson/6' },
    { id: 'Lesson 7', label: 'Launch Plan', icon: FiFileText, path: '/lesson/7' },
    { id: 'Lesson 8', label: 'Beyond Launch', icon: FiBookOpen, path: '/lesson/8' }
  ];

  const allTabs = [...mainTabs, ...lessonTabs];

  const handleTabClick = (tab) => {
    setActiveTab(tab.id);
    navigate(tab.path);
    setIsMenuOpen(false);
  };

  const getCurrentTab = () => {
    if (location.pathname === '/') return 'Dashboard';
    if (location.pathname === '/summary') return 'Summary';
    if (location.pathname.startsWith('/lesson/')) {
      const lessonNumber = location.pathname.split('/')[2];
      return `Lesson ${lessonNumber}`;
    }
    return 'Dashboard';
  };

  const currentTab = getCurrentTab();

  const menuVariants = {
    closed: {
      opacity: 0,
      scale: 0.95,
      transition: {
        duration: 0.2,
        ease: "easeInOut"
      }
    },
    open: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.2,
        ease: "easeInOut"
      }
    }
  };

  const itemVariants = {
    closed: { opacity: 0, y: -10 },
    open: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.05,
        duration: 0.3,
        ease: "easeOut"
      }
    })
  };

  return (
    <header className="bg-white shadow-soft sticky top-0 z-50 border-b border-gray-100">
      <div className="px-6 py-6">
        {/* Brand Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-primary to-blue-600 rounded-lg flex items-center justify-center shadow-soft">
              <FiFeather className="text-white text-xl" />
            </div>
            <div>
              <h1 className="font-serif text-2xl md:text-3xl text-primary font-bold leading-tight">
                Write. Refine. Release.
              </h1>
              <p className="font-sans text-sm text-gray-500 font-medium tracking-wide uppercase mt-1">
                Cohort Companion Guide
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="font-serif text-sm text-gray-600 font-medium">
              CROSS + PEN
            </p>
            <p className="font-sans text-xs text-accent font-semibold tracking-wide">
              GospelWise Author
            </p>
          </div>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden lg:block">
          <div className="flex items-center gap-4">
            {/* Main Navigation Tabs */}
            <div className="flex gap-1">
              {mainTabs.map((tab) => {
                const isActive = currentTab === tab.id;
                return (
                  <motion.button
                    key={tab.id}
                    onClick={() => handleTabClick(tab)}
                    className={`nav-tab relative px-6 py-3 rounded-t-lg font-medium font-sans text-sm transition-all duration-300 flex items-center gap-2 ${
                      isActive
                        ? 'text-primary bg-blue-50 border-b-2 border-primary'
                        : 'text-gray-600 hover:text-primary hover:bg-gray-50'
                    }`}
                    whileHover={{ y: -1 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <SafeIcon 
                      icon={tab.icon} 
                      className={`text-base ${isActive ? 'text-primary' : 'text-gray-400'}`} 
                    />
                    <span className="whitespace-nowrap">{tab.label}</span>
                    {isActive && (
                      <motion.div
                        layoutId="activeTab"
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent"
                        initial={false}
                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      />
                    )}
                  </motion.button>
                );
              })}
            </div>

            {/* Lessons Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`px-6 py-3 rounded-t-lg font-medium font-sans text-sm transition-all duration-300 flex items-center gap-2 ${
                  currentTab.startsWith('Lesson')
                    ? 'text-primary bg-blue-50 border-b-2 border-primary'
                    : 'text-gray-600 hover:text-primary hover:bg-gray-50'
                }`}
              >
                <SafeIcon icon={FiBook} className="text-base" />
                <span>Lessons</span>
                <motion.div
                  animate={{ rotate: isMenuOpen ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <FiIcons.FiChevronDown className="text-base" />
                </motion.div>
              </button>

              {/* Dropdown Menu */}
              <AnimatePresence>
                {isMenuOpen && (
                  <motion.div
                    variants={menuVariants}
                    initial="closed"
                    animate="open"
                    exit="closed"
                    className="absolute top-full left-0 mt-2 w-80 bg-white rounded-xl shadow-medium border border-gray-100 overflow-hidden"
                  >
                    <div className="p-2">
                      <div className="px-4 py-2 text-xs font-sans font-semibold text-gray-500 uppercase tracking-wider">
                        8 Steps to Refine & Release
                      </div>
                      {lessonTabs.map((tab, index) => {
                        const isActive = currentTab === tab.id;
                        return (
                          <motion.button
                            key={tab.id}
                            custom={index}
                            variants={itemVariants}
                            onClick={() => handleTabClick(tab)}
                            className={`w-full px-4 py-3 rounded-lg font-sans text-sm transition-all duration-300 flex items-center gap-3 text-left ${
                              isActive
                                ? 'bg-primary text-white'
                                : 'text-gray-700 hover:bg-gray-50 hover:text-primary'
                            }`}
                          >
                            <SafeIcon 
                              icon={tab.icon} 
                              className={`text-base ${isActive ? 'text-white' : 'text-gray-400'}`} 
                            />
                            <div className="flex-1">
                              <div className="font-medium">{tab.label}</div>
                              <div className={`text-xs ${isActive ? 'text-blue-100' : 'text-gray-500'}`}>
                                {tab.id}
                              </div>
                            </div>
                          </motion.button>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </nav>

        {/* Mobile Navigation */}
        <nav className="lg:hidden">
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              {mainTabs.map((tab) => {
                const isActive = currentTab === tab.id;
                return (
                  <motion.button
                    key={tab.id}
                    onClick={() => handleTabClick(tab)}
                    className={`px-4 py-3 rounded-lg font-medium font-sans text-sm transition-all duration-300 flex items-center gap-2 ${
                      isActive
                        ? 'bg-primary text-white shadow-medium'
                        : 'bg-white text-gray-600 hover:bg-gray-50 hover:text-primary border border-gray-200'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <SafeIcon icon={tab.icon} className="text-base" />
                    <span className="whitespace-nowrap">{tab.label}</span>
                  </motion.button>
                );
              })}
            </div>

            {/* Mobile Menu Button */}
            <motion.button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`p-3 rounded-lg font-medium transition-all duration-300 ${
                currentTab.startsWith('Lesson')
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="text-xl" />
            </motion.button>
          </div>

          {/* Mobile Menu */}
          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                variants={menuVariants}
                initial="closed"
                animate="open"
                exit="closed"
                className="absolute top-full left-6 right-6 mt-2 bg-white rounded-xl shadow-medium border border-gray-100 overflow-hidden"
              >
                <div className="p-2">
                  <div className="px-4 py-2 text-xs font-sans font-semibold text-gray-500 uppercase tracking-wider">
                    8 Steps to Refine & Release
                  </div>
                  {lessonTabs.map((tab, index) => {
                    const isActive = currentTab === tab.id;
                    return (
                      <motion.button
                        key={tab.id}
                        custom={index}
                        variants={itemVariants}
                        onClick={() => handleTabClick(tab)}
                        className={`w-full px-4 py-3 rounded-lg font-sans text-sm transition-all duration-300 flex items-center gap-3 text-left ${
                          isActive
                            ? 'bg-primary text-white'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-primary'
                        }`}
                      >
                        <SafeIcon 
                          icon={tab.icon} 
                          className={`text-base ${isActive ? 'text-white' : 'text-gray-400'}`} 
                        />
                        <div className="flex-1">
                          <div className="font-medium">{tab.label}</div>
                          <div className={`text-xs ${isActive ? 'text-blue-100' : 'text-gray-500'}`}>
                            {tab.id}
                          </div>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </nav>
      </div>
    </header>
  );
};

export default Navigation;